/**
 * Clients → Meetings (prototype VIEWS.meetings L1292, meetList L1291, openMeet L1296, newMeet/mtMode/saveMeet
 * L1924–1984). Also used inside the client panel (a client's meetings) and by the deep link the API puts on tasks
 * made from minutes: /clients/book?client=…&tab=meetings&meeting=….
 *
 * Data: GET /meetings (rows + the four stats; the server scopes a member to the clients they are SPOC of),
 * GET /meetings/{id}, POST /meetings (always a draft) then POST /meetings/{id}/confirm from the same "Save minutes"
 * press for typed minutes (step-8 decision 2), POST /meetings/document/read (a minutes file is read, shown, then
 * submitted as POST /meetings with transcript_source document), GET /meetings/{id}/media-url to open legacy audio,
 * GET /settings/privacy for the consent tick. The server takes no audio; live transcripts come from the browser.
 */
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useSearchParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { DROP_REASON_LABEL, type DropReason, type Meeting, type MeetingListItem, type MeetingTask } from '@amplispot/contracts';
import { call } from '../../api';
import { useSession } from '../../lib/session';
import { firstName } from '../../lib/format';
import { dayMonth } from '../../lib/timesheets';
import { isProcessing, justAddedMeeting, lengthLabel, mkeys, statusChip, useMeeting, useMeetingWrite, useMeetings } from '../../lib/meetings';
import { Drawer, Empty, ErrorState, Loading, errorText, useFail, useToast } from '../../ui/kit';
import { Icon } from '../../ui/Icon';
import { Stat } from '../timesheets/TimesheetReport';

const RO_TITLE = 'Read-only while you view as someone';
const REASONS = Object.entries(DROP_REASON_LABEL) as [DropReason, string][];

// ================================================================ the tab

export function Meetings() {
  const { readOnly } = useSession();
  const [params, setParams] = useSearchParams();
  const openId = params.get('meeting');
  const [logging, setLogging] = useState(false);
  const q = useMeetings(null);
  const setOpen = (id: string | null) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        if (id) n.set('meeting', id);
        else n.delete('meeting');
        return n;
      },
      { replace: !id },
    );
  const stats = q.data?.pages[0]?.stats;

  return (
    <>
      <div className="ph" style={{ margin: '0 0 12px', justifyContent: 'flex-end' }}>
        <div className="r" style={{ marginLeft: 0 }}>
          <button className="btn pri" onClick={() => setLogging(true)} disabled={readOnly} title={readOnly ? RO_TITLE : undefined}>
            <Icon name="mic" size={15} /> Log a meeting
          </button>
        </div>
      </div>
      {q.isPending ? (
        <div className="panel">
          <Loading label="Loading meetings…" />
        </div>
      ) : q.isError ? (
        <div className="panel">
          <ErrorState error={q.error} onRetry={() => q.refetch()} />
        </div>
      ) : (
        <>
          {stats ? (
            <div className="stats">
              <Stat k="This month" v={stats.this_month} />
              <Stat k="Awaiting confirmation" v={stats.awaiting_confirmation} cls="amber" />
              <Stat k="Tasks created from minutes" v={stats.tasks_created} cls="green" />
              <Stat k="Decisions logged" v={stats.decisions_logged} />
            </div>
          ) : null}
          <MeetingRows q={q} onOpen={setOpen} hi={openId} />
        </>
      )}
      {logging ? <LogMeetingModal onClose={() => setLogging(false)} onSubmitted={setOpen} /> : null}
      {openId ? <MeetingDrawer key={openId} id={openId} onClose={() => setOpen(null)} onOpen={setOpen} /> : null}
    </>
  );
}

// ================================================================ meetList

type MeetingsQuery = ReturnType<typeof useMeetings>;

/** The prototype's `meetList`: one row per meeting with its media chip and status chip; "Show more" pages on. */
/** `hi` is the meeting to tint (the one open in the review); otherwise the one just added in this tab (frames 19-21). */
export function MeetingRows({ q, onOpen, emptyText, hi }: { q: MeetingsQuery; onOpen: (id: string) => void; emptyText?: string; hi?: string | null }) {
  if (q.isPending) return <div className="panel"><Loading label="Loading meetings…" /></div>;
  if (q.isError) return <div className="panel"><ErrorState error={q.error} onRetry={() => q.refetch()} /></div>;
  const rows = q.data.pages.flatMap((p) => p.data);
  const tint = hi ?? justAddedMeeting();
  return (
    <div className="panel">
      {rows.length ? (
        rows.map((m) => <MeetingRow key={m.id} m={m} onOpen={onOpen} hi={m.id === tint} />)
      ) : (
        <Empty title="No meetings yet">{emptyText ?? 'Press Log a meeting to add the minutes of a client meeting.'}</Empty>
      )}
      {q.hasNextPage ? (
        <div style={{ padding: '10px 16px' }}>
          <button className="btn sm" onClick={() => void q.fetchNextPage()} disabled={q.isFetchingNextPage}>
            {q.isFetchingNextPage ? 'Loading…' : 'Show more'}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function mediaChip(m: MeetingListItem): [string, string] {
  if (m.transcript_source === 'live') return ['brand', 'Live transcript'];
  if (m.transcript_source === 'document') return ['info', 'Document'];
  if (m.media?.kind === 'audio') return ['brand', 'Audio'];
  if (m.media?.kind === 'link') return ['info', 'Recording link'];
  if (m.media_kind === 'upload') return ['info', 'Minutes file'];
  return ['info', 'Typed minutes'];
}

function MeetingRow({ m, onOpen, hi }: { m: MeetingListItem; onOpen: (id: string) => void; hi: boolean }) {
  const [mc, ml] = mediaChip(m);
  const [sc, sl] = statusChip(m);
  return (
    <div
      className="li click"
      role="button"
      tabIndex={0}
      aria-current={hi || undefined}
      style={hi ? { background: 'var(--brand-bg)' } : undefined}
      onClick={() => onOpen(m.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(m.id);
        }
      }}
    >
      <div style={{ color: 'var(--brand)' }}>
        <Icon name={m.media?.kind === 'audio' || m.transcript_source === 'live' ? 'mic' : 'calendar'} size={18} style={{ strokeWidth: 1.7 }} />
      </div>
      <div className="b">
        <div className="t">{m.title}</div>
        <div className="m">
          <span>{m.client.name}</span>
          <span>{dayMonth(m.held_on)}</span>
          <span>{lengthLabel(m)}</span>
          <span>by {firstName(m.held_by.name)}</span>
        </div>
      </div>
      <span className={`chip ${mc}`}>{ml}</span>
      <span className={`chip ${sc}`}>{sl}</span>
    </div>
  );
}

// ================================================================ Log a meeting (v2: LogMeeting.tsx)

import { LogMeetingModal } from './LogMeeting';
export { LogMeetingModal };

/** Decisions as editable lines: fix the wording, drop one, or add one (same 3–300 characters the server takes). */
function DecisionList({ value, onChange, add }: { value: string[]; onChange: (v: string[]) => void; add?: boolean }) {
  return (
    <div style={{ marginBottom: 6 }}>
      {value.map((d, i) => (
        <div key={i} className="split" style={{ marginBottom: 6, gap: 8 }}>
          <input
            className="in"
            style={{ flex: 1 }}
            value={d}
            onChange={(e) => onChange(value.map((x, j) => (j === i ? e.target.value : x)))}
            aria-label={`Decision ${i + 1}`}
            placeholder="What was decided"
          />
          <button className="btn sm" onClick={() => onChange(value.filter((_, j) => j !== i))} aria-label={`Remove decision ${i + 1}`} title="Remove">
            ✕
          </button>
        </div>
      ))}
      {add && value.length < 50 ? (
        <button className="btn sm" onClick={() => onChange([...value, ''])}>
          + Add a decision
        </button>
      ) : null}
    </div>
  );
}

/** The 3–300 / 3–200 / 50-item limits the server applies (contracts/meetings.ts), checked before anything is sent. */
function listProblem(tasks: string[], decisions: string[]): string | null {
  if (tasks.length > 50) return 'Keep it to 50 tasks. Leave out the rest, or log a second meeting.';
  if (tasks.some((t) => t.length < 3)) return 'Say what each task is in a few words';
  if (tasks.some((t) => t.length > 200)) return 'Keep each task under 200 characters';
  if (decisions.length > 50) return 'Keep it to 50 decisions';
  if (decisions.some((d) => d.length < 3)) return 'Write each decision in a few words';
  if (decisions.some((d) => d.length > 300)) return 'Keep each decision under 300 characters';
  return null;
}

// ================================================================ the drawer (openMeet)

interface Tick {
  on: boolean;
  title: string;
  why: DropReason;
  note: string;
}

export function MeetingDrawer({ id, onClose, onOpen }: { id: string; onClose: () => void; onOpen?: (id: string) => void }) {
  const q = useMeeting(id);
  const { readOnly } = useSession();
  const { fail, toast } = useFail([]);
  const [ticks, setTicks] = useState<Record<string, Tick>>({});
  const qc = useQueryClient();
  // The draft's own words, editable until it is confirmed: summary, decisions, and tasks the person adds.
  const [summary, setSummary] = useState('');
  const [decs, setDecs] = useState<string[]>([]);
  const [extra, setExtra] = useState<{ key: number; title: string }[]>([]);
  const [dirty, setDirty] = useState(false);
  const nextKey = useRef(1);
  const m = q.data;

  useEffect(() => {
    // Take the server's text whenever it changes (draft arrives, someone else edited) unless the person is mid-edit.
    if (!m || dirty) return;
    setSummary(m.summary ?? '');
    setDecs(m.decisions.map((d) => d.text));
  }, [m, dirty]);

  useEffect(() => {
    if (!m) return;
    setTicks((cur) => {
      const n: Record<string, Tick> = {};
      for (const t of m.proposed_tasks) n[t.id] = cur[t.id] ?? { on: true, title: t.title, why: 'not_needed', note: '' };
      return n;
    });
  }, [m]);

  const added = extra.map((x) => x.title.trim()).filter(Boolean);
  const cleanDecs = decs.map((d) => d.trim()).filter(Boolean);
  const edited = !!m && (summary.trim() !== (m.summary ?? '').trim() || cleanDecs.join('\n') !== m.decisions.map((d) => d.text).join('\n') || added.length > 0);

  const confirm = useMeetingWrite(async (start: Meeting) => {
    let mm = start;
    if (edited) {
      // Save the edits first (PATCH replaces the lists, so the staged tasks are sent back with their dates).
      mm = await call('meetings.update', {
        params: { id: start.id },
        ifMatch: start.version,
        body: {
          summary: summary.trim() || null,
          decisions: cleanDecs,
          proposed_tasks: [
            ...start.proposed_tasks.map((t) => ({ id: t.id, title: (ticks[t.id]?.title ?? t.title).trim(), suggested_due: t.suggested_due ?? undefined })),
            ...added.map((title) => ({ title })),
          ],
        },
      });
      // The saved draft is now the one on screen: a failed confirm retries against it, with the edits kept.
      qc.setQueryData(mkeys.one(mm.id), mm);
      setDirty(false);
      setExtra([]);
    }
    const before = new Set(start.proposed_tasks.map((t) => t.id));
    return call('meetings.confirm', {
      params: { id: mm.id },
      ifMatch: mm.version,
      body: {
        tasks: mm.proposed_tasks.map((t) => {
          // A task the person added just now is one they want.
          if (!before.has(t.id)) return { meeting_task_id: t.id, accept: true };
          const k = ticks[t.id] ?? { on: true, title: t.title, why: 'not_needed' as DropReason, note: '' };
          const title = k.title.trim() !== t.title ? k.title.trim() : undefined;
          return k.on
            ? { meeting_task_id: t.id, accept: true, title }
            : { meeting_task_id: t.id, accept: false, drop_reason_code: k.why, drop_note: k.why === 'other' ? k.note.trim() || undefined : undefined };
        }),
      },
    });
  });

  const processing = m ? isProcessing(m) : false;
  const canConfirm = !!m && m.status === 'draft' && m.can_edit && !processing && !readOnly;

  const doConfirm = () => {
    if (!m) return;
    for (const t of m.proposed_tasks) {
      const k = ticks[t.id];
      if (k?.on && k.title.trim().length < 3) return toast('Say what each task is in a few words');
      if (k && !k.on && k.why === 'other' && !k.note.trim()) return toast('Add a note when the reason is Other');
    }
    if (summary.trim().length > 1000) return toast('Keep the summary under 1,000 characters');
    const problem = listProblem([...m.proposed_tasks.map((t) => (ticks[t.id]?.title ?? t.title).trim()), ...added], cleanDecs);
    if (problem) return toast(problem);
    confirm.mutate(m, {
      onSuccess: () => {
        toast('Minutes confirmed. Tasks added to the list.');
        onClose();
      },
      onError: fail,
    });
  };

  return (
    <Drawer
      title="Meeting minutes"
      onClose={onClose}
      footer={
        m ? (
          <>
            <div className="grow" />
            <button className="btn" onClick={onClose}>
              Close
            </button>
            {canConfirm ? (
              <button className="btn pri" onClick={doConfirm} disabled={confirm.isPending}>
                {confirm.isPending ? 'Confirming…' : 'Confirm minutes'}
              </button>
            ) : null}
          </>
        ) : undefined
      }
    >
      {q.isPending ? (
        <Loading label="Loading the meeting…" />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : (
        <>
          <h2 style={{ fontSize: 18 }}>{m!.title}</h2>
          <div style={{ margin: '8px 0 14px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <span className="chip">{m!.client.name}</span>
            <span className="chip">{dayMonth(m!.held_on)}</span>
            <span className={`chip ${m!.status === 'confirmed' ? 'hot' : processing ? 'info' : 'warm'}`}>
              {processing ? <span className="spin" aria-hidden="true" style={{ width: 11, height: 11, borderWidth: 2, verticalAlign: '-1px' }} /> : null}
              {processing ? ' ' : null}
              {m!.status === 'confirmed' ? 'Confirmed' : processing ? (m!.transcript_state === 'queued' || m!.transcript_state === 'running' ? 'Transcribing' : 'Reading the minutes') : 'Draft'}
            </span>
            <SourceChip m={m!} />
            {m!.retranscribed_from_id ? (
              <button className="chip" style={{ cursor: 'pointer', border: 0 }} onClick={() => onOpen?.(m!.retranscribed_from_id!)}>
                Transcribed again from an earlier draft
              </button>
            ) : null}
          </div>

          {m!.attendees ? (
            <div className="sub" style={{ marginBottom: 12 }}>
              Who was there: {m!.attendees}
            </div>
          ) : null}
          <MediaPanel m={m!} />
          <StateNote m={m!} canEdit={canConfirm} />

          <div className="lbl" style={{ marginBottom: 6 }}>
            Summary
          </div>
          {canConfirm ? (
            <textarea
              className="in"
              rows={3}
              style={{ marginBottom: 16 }}
              value={summary}
              onChange={(e) => {
                setDirty(true);
                setSummary(e.target.value);
              }}
              aria-label="Summary"
              placeholder="What the meeting was about and what came out of it"
            />
          ) : processing && !m!.summary ? (
            <Skel widths={[94, 70]} style={{ marginBottom: 16 }} />
          ) : (
            <p style={{ marginBottom: 16 }}>{m!.summary ?? <span className="sub">No summary written.</span>}</p>
          )}
          {m!.minutes_text ? (
            <>
              <div className="lbl" style={{ marginBottom: 6 }}>
                Minutes
              </div>
              <MinutesBox text={m!.minutes_text} />
            </>
          ) : null}
          {processing ? null : (
            <>
              <div className="lbl" style={{ marginBottom: 6 }}>
                Decisions
              </div>
              {canConfirm ? (
                <div style={{ marginBottom: 16 }}>
                  <DecisionList
                    value={decs}
                    add
                    onChange={(v) => {
                      setDirty(true);
                      setDecs(v);
                    }}
                  />
                </div>
              ) : (
                <ul style={{ margin: '0 0 16px 18px' }}>
                  {m!.decisions.length ? m!.decisions.map((d) => <li key={d.id}>{d.text}</li>) : <li>None recorded</li>}
                </ul>
              )}
            </>
          )}
          <div className="lbl" style={{ marginBottom: 6 }}>
            Tasks from this meeting
          </div>
          {canConfirm ? (
            <>
              {m!.proposed_tasks.map((t) => (
                <StagedTask key={t.id} t={t} k={ticks[t.id]} set={(p) => setTicks((all) => ({ ...all, [t.id]: { ...all[t.id]!, ...p } }))} />
              ))}
              {extra.map((x, i) => (
                <div key={x.key} className="split" style={{ marginBottom: 8, gap: 8 }}>
                  <input
                    className="in"
                    style={{ flex: 1 }}
                    value={x.title}
                    onChange={(e) => setExtra((all) => all.map((y, j) => (j === i ? { ...y, title: e.target.value } : y)))}
                    aria-label="New task"
                    placeholder="What has to be done, by whom"
                  />
                  <button className="btn sm" onClick={() => setExtra((all) => all.filter((_, j) => j !== i))} aria-label="Remove task" title="Remove">
                    ✕
                  </button>
                </div>
              ))}
              {!m!.proposed_tasks.length && !extra.length ? (
                <div className="sub" style={{ marginBottom: 6 }}>
                  None yet.
                </div>
              ) : null}
              <button className="btn sm" onClick={() => setExtra((all) => [...all, { key: nextKey.current++, title: '' }])}>
                + Add a task
              </button>
              <div className="sub" style={{ fontSize: 12, marginTop: 4 }}>
                Added tasks go to the client’s SPOC, due in 3 days.
              </div>
            </>
          ) : processing && !m!.proposed_tasks.length ? (
            <Skel widths={[80, 66, 74]} />
          ) : m!.proposed_tasks.length ? (
            m!.proposed_tasks.map((t) => <DoneTask key={t.id} t={t} draft={m!.status === 'draft'} />)
          ) : (
            <div className="sub" style={{ color: 'var(--sub)' }}>
              None yet
            </div>
          )}
          {m!.status === 'confirmed' && m!.confirmed_by ? (
            <div className="sub" style={{ marginTop: 14, fontSize: 12.5 }}>
              Confirmed by {m!.confirmed_by.name}
              {m!.confirmed_at ? ` on ${dayMonth(m!.confirmed_at.slice(0, 10))}` : ''}.
            </div>
          ) : null}
        </>
      )}
    </Drawer>
  );
}

/** Where the minutes came from (frames 19-20): a live browser transcript or a read document. */
function SourceChip({ m }: { m: Meeting }) {
  if (m.transcript_source === 'live') {
    return (
      <span className="chip brand">
        <Icon name="mic" size={12} /> Live transcript
        {m.length_minutes != null ? ` · ${m.length_minutes} min` : ''}
      </span>
    );
  }
  if (m.transcript_source === 'document') return <span className="chip info">Document</span>;
  return null;
}

/** Skeleton lines standing in for text that is still being read (app.css .skl/.skel). */
function Skel({ widths, style }: { widths: number[]; style?: CSSProperties }) {
  return (
    <div className="skl" aria-busy="true" aria-label="Loading" style={style}>
      {widths.map((w, i) => (
        <div key={i} className="skel" style={{ width: `${w}%` }} />
      ))}
    </div>
  );
}

/** The submitted minutes, clipped with a fade when long (frames 19-20); "Show all" opens it out. */
function MinutesBox({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const long = text.length > 420 || text.split('\n').length > 6;
  const clip = long && !open;
  return (
    <div style={{ marginBottom: 16 }}>
      <div
        style={{
          whiteSpace: 'pre-wrap',
          fontSize: 13.5,
          background: 'var(--canvas)',
          padding: 12,
          borderRadius: 10,
          ...(clip ? { maxHeight: 118, overflow: 'hidden', position: 'relative' } : {}),
        }}
      >
        {text}
        {clip ? (
          <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 44, background: 'linear-gradient(transparent, var(--canvas))' }} />
        ) : null}
      </div>
      {long ? (
        <button className="btn sm ghost" style={{ marginTop: 6 }} onClick={() => setOpen((o) => !o)}>
          {open ? 'Show less' : 'Show all'}
        </button>
      ) : null}
    </div>
  );
}

function MediaPanel({ m }: { m: Meeting }) {
  const toast = useToast();
  const [src, setSrc] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const media = m.media;
  if (!media) return null;
  if (media.kind === 'link') {
    return (
      <div className="note" style={{ marginBottom: 16 }}>
        Recording stored as a link only. Our system does not hold a copy.
        {media.url ? (
          <>
            {' '}
            <a href={media.url} target="_blank" rel="noreferrer noopener" style={{ color: 'var(--brand)', fontWeight: 600 }}>
              Open the recording
            </a>
          </>
        ) : null}
      </div>
    );
  }
  const open = async () => {
    setBusy(true);
    try {
      const r = await call('meetings.mediaUrl', { params: { id: m.id } });
      if (media.kind === 'audio') setSrc(r.url);
      else window.open(r.url, '_blank', 'noopener');
    } catch (e) {
      toast(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const audio = media.kind === 'audio';
  const len = m.length_minutes != null ? `${m.length_minutes} min` : media.filename ?? 'Recording';
  const meta = audio
    ? media.deleted
      ? `${len} · deleted under the retention setting`
      : `${len} · restricted access · ${media.delete_after ? `deleted after ${dayMonth(media.delete_after)}` : 'kept until deleted'}`
    : media.filename ?? 'Minutes file';
  return (
    <div className="panel" style={{ marginBottom: 16 }}>
      <div className="li">
        <div style={{ color: 'var(--brand)' }}>
          <Icon name={audio ? 'mic' : 'calendar'} size={18} style={{ strokeWidth: 1.7 }} />
        </div>
        <div className="b">
          <div className="t">{audio ? 'Audio recording' : 'Minutes file'}</div>
          <div className="m">{meta}</div>
        </div>
        {media.can_open && !media.deleted && !src ? (
          <button className="btn sm" onClick={() => void open()} disabled={busy}>
            {busy ? 'Opening…' : audio ? 'Play' : 'Open'}
          </button>
        ) : null}
      </div>
      {src ? (
        <div style={{ padding: '0 16px 12px' }}>
          <audio src={src} controls autoPlay style={{ width: '100%' }} />
        </div>
      ) : null}
    </div>
  );
}

/** Transcript and extraction state, and the problem line when either failed (guide §9). */
function StateNote({ m, canEdit }: { m: Meeting; canEdit: boolean }) {
  const tr = m.transcript_state;
  const ex = m.extraction_state;
  if (m.status === 'confirmed') return null;
  if (tr === 'failed' || ex === 'failed') {
    return (
      <div className="note" style={{ marginBottom: 16, borderColor: 'var(--cold)' }}>
        <b>{tr === 'failed' ? 'The transcript failed.' : 'The tasks could not be found in these minutes.'}</b> {m.problem ?? (canEdit ? 'Type the summary and the tasks below, then confirm.' : 'The person who logged it can add the summary and the tasks.')}
      </div>
    );
  }
  if (tr === 'queued' || tr === 'running') {
    return (
      <div className="note" style={{ marginBottom: 16 }}>
        {tr === 'queued' ? 'Waiting for the transcription server. ' : 'Transcribing now. '}
        Draft minutes and tasks will appear here for you to confirm. You can close this; the draft stays in the list.
      </div>
    );
  }
  if (ex === 'queued' || ex === 'running') {
    return (
      <div className="note" style={{ marginBottom: 16 }}>
        Reading the minutes now. The summary, decisions and tasks will appear here for you to confirm. You can close this; the draft stays in the list.
      </div>
    );
  }
  return null;
}

/** A staged task on a draft: tick to make it a task, or untick and say why (prototype propRender). */
function StagedTask({ t, k, set }: { t: MeetingTask; k: Tick | undefined; set: (p: Partial<Tick>) => void }) {
  const on = k?.on ?? true;
  return (
    <div className="split" style={{ marginBottom: 8, gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <input type="checkbox" checked={on} onChange={(e) => set({ on: e.target.checked })} style={{ marginTop: 10 }} aria-label="Make this a task" />
      <div className="grow" style={{ minWidth: 200 }}>
        <input className="in" value={k?.title ?? t.title} onChange={(e) => set({ title: e.target.value })} aria-label="Task" />
        <div className="sub" style={{ fontSize: 12, marginTop: 2 }}>
          {[t.suggested_assignee ? `For ${firstName(t.suggested_assignee.name)}` : null, t.suggested_due ? `due ${dayMonth(t.suggested_due)}` : null].filter(Boolean).join(', ')}
          {t.quote ? `${t.suggested_assignee || t.suggested_due ? ' · ' : ''}“${t.quote}”` : ''}
        </div>
      </div>
      {on ? null : (
        <select className="in" style={{ width: 'auto' }} value={k?.why ?? 'not_needed'} onChange={(e) => set({ why: e.target.value as DropReason })} title="Why leave it out">
          {REASONS.map(([r, l]) => (
            <option key={r} value={r}>
              {l}
            </option>
          ))}
        </select>
      )}
      {!on && k?.why === 'other' ? <input className="in" style={{ flex: '1 1 100%' }} value={k.note} onChange={(e) => set({ note: e.target.value })} placeholder="Why it is left out" /> : null}
    </div>
  );
}

function DoneTask({ t, draft }: { t: MeetingTask; draft: boolean }) {
  const [cls, label] = t.accepted === true ? ['brand', 'Task'] : t.accepted === false ? ['', 'Left out'] : ['warm', draft ? 'Proposed' : 'Task'];
  return (
    <div className="li" style={{ padding: '8px 0' }}>
      <span className={`chip ${cls}`}>{label}</span>
      <div className="b">
        {t.title}
        {t.suggested_assignee || t.suggested_due ? (
          <div className="sub" style={{ fontSize: 12 }}>
            {[t.suggested_assignee ? firstName(t.suggested_assignee.name) : null, t.suggested_due ? `due ${dayMonth(t.suggested_due)}` : null].filter(Boolean).join(', ')}
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ================================================================ a client's meetings (client panel)

/** The prototype Client 360 "Meetings" tab: that client's meetings, with Log a meeting for this client. */
export function ClientMeetings({ clientId, onOpen }: { clientId: string; onOpen: (id: string) => void }) {
  const q = useMeetings(clientId);
  return <MeetingRows q={q} onOpen={onOpen} emptyText="Log a meeting to keep its minutes and turn its actions into tasks." />;
}
